/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Smart DataTable Output v2 için saf snapshot, redaksiyon ve iş durumu yardımcıları.
 * Tek kaynak qubepro-core'dur; uydulara resmî sync ile dağıtılır.
 */

import type {
  DataTableOutputCell,
  DataTableOutputColumnType,
  DataTableOutputDataset,
  DataTableOutputExportJob,
  DataTableOutputFormat,
} from './DataTableOutputExport';

const SENSITIVE_DATASET_COLUMN_TOKEN = /^(?:password|passwd|pwd|secret|token|authorization|credential|apikey|connectionstring|databaseurl)$/u;
const SENSITIVE_DATASET_PARTS = new Set(['password', 'passwd', 'pwd', 'secret', 'token', 'authorization', 'credential']);

export function isSensitiveDataTableOutputIdentifier(value: string): boolean {
  const normalized = value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  const parts = normalized.split(/\s+/).filter(Boolean);
  const compact = parts.join('');
  return SENSITIVE_DATASET_COLUMN_TOKEN.test(compact)
    || parts.some((token) => SENSITIVE_DATASET_PARTS.has(token));
}

function normalizeFormat(value: unknown, fallback: DataTableOutputFormat = 'PDF'): DataTableOutputFormat {
  const normalized = String(value || fallback).toUpperCase();
  return normalized === 'XLSX' || normalized === 'CSV' || normalized === 'PDF' ? normalized : fallback;
}

export function extractDataTableOutputApiData(value: unknown): unknown {
  let current = value;
  for (let depth = 0; depth < 2; depth += 1) {
    if (!current || typeof current !== 'object') break;
    const payload = current as { data?: unknown; success?: unknown };
    if (payload.success === true && payload.data !== undefined) {
      current = payload.data;
      continue;
    }
    // Ürün-local ALL_FILTERED_ROWS sağlayıcısı Axios cevabını doğrudan döndürebilir.
    if ('data' in payload && payload.data !== undefined) {
      current = payload.data;
      continue;
    }
    break;
  }
  return current;
}

export function normalizeDataTableOutputJob(
  value: unknown,
  fallback?: DataTableOutputExportJob | null,
): DataTableOutputExportJob {
  const raw = extractDataTableOutputApiData(value);
  const job = raw && typeof raw === 'object' ? raw as Record<string, unknown> : {};
  const statusValue = String(job.status || fallback?.status || 'QUEUED').toUpperCase();
  const allowedStatuses: DataTableOutputExportJob['status'][] = [
    'PREPARING', 'QUEUED', 'RUNNING', 'RETRYING', 'SUCCESS', 'FAILED', 'DEAD', 'CANCELLED',
  ];
  const status = allowedStatuses.includes(statusValue as DataTableOutputExportJob['status'])
    ? statusValue as DataTableOutputExportJob['status']
    : 'FAILED';
  const defaultStage: DataTableOutputExportJob['stage'] = status === 'SUCCESS'
    ? 'READY'
    : status === 'FAILED' || status === 'DEAD' || status === 'CANCELLED'
      ? 'FAILED'
      : status === 'PREPARING'
        ? 'PREPARING'
        : status === 'QUEUED' || status === 'RETRYING'
          ? 'QUEUED'
          : 'RENDERING';
  const stageValue = String(job.stage || fallback?.stage || defaultStage).toUpperCase();
  const allowedStages: DataTableOutputExportJob['stage'][] = [
    'PREPARING', 'QUEUED', 'RENDERING', 'ARCHIVING', 'READY', 'FAILED',
  ];
  return {
    id: job.id === null || job.id === undefined ? (fallback?.id ?? null) : String(job.id),
    format: normalizeFormat(job.format, fallback?.format),
    status,
    stage: allowedStages.includes(stageValue as DataTableOutputExportJob['stage'])
      ? stageValue as DataTableOutputExportJob['stage']
      : defaultStage,
    fileName: job.fileName === null || job.fileName === undefined ? (fallback?.fileName ?? null) : String(job.fileName),
    recordCount: Number(job.recordCount ?? fallback?.recordCount ?? 0),
    columnCount: Number(job.columnCount ?? fallback?.columnCount ?? 0),
    redactedColumnCount: Number(job.redactedColumnCount ?? fallback?.redactedColumnCount ?? 0),
    archived: Boolean(job.archived ?? fallback?.archived ?? false),
    errorMessage: job.errorMessage === null || job.errorMessage === undefined ? (fallback?.errorMessage ?? null) : String(job.errorMessage),
    createdAt: job.createdAt === null || job.createdAt === undefined ? (fallback?.createdAt ?? null) : String(job.createdAt),
    finishedAt: job.finishedAt === null || job.finishedAt === undefined ? (fallback?.finishedAt ?? null) : String(job.finishedAt),
    durationMs: Number.isFinite(Number(job.durationMs)) ? Number(job.durationMs) : (fallback?.durationMs ?? null),
    downloadUrl: job.downloadUrl === null || job.downloadUrl === undefined ? (fallback?.downloadUrl ?? null) : String(job.downloadUrl),
  };
}

function outputCellValue(value: unknown): DataTableOutputCell {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') {
    const redacted = value
      .replace(/(?:postgres(?:ql)?|mysql|mssql|mongodb(?:\+srv)?|redis):\/\/[^\s]+/giu, '[GİZLENDİ]')
      .replace(/\b(password|passwd|pwd|secret|token|api[-_ ]?key|authorization|credential)\s*[:=]\s*[^\s,;]+/giu, '$1=[GİZLENDİ]')
      .replace(/\bBearer\s+[A-Za-z0-9._~+\/-]+=*/giu, 'Bearer [GİZLENDİ]')
      .replace(/\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/gu, '[GİZLENDİ]');
    if (redacted.length > 500) throw new Error('DataTable çıktı değeri 500 karakteri aşamaz.');
    return redacted;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    if (typeof value === 'number' && !Number.isFinite(value)) {
      throw new Error('DataTable çıktı değeri sonlu sayı olmalıdır.');
    }
    return value;
  }
  throw new Error('DataTable çıktı hücresi yalnız primitive değer içerebilir.');
}

function inferOutputColumnType(values: DataTableOutputCell[]): DataTableOutputColumnType {
  const populated = values.filter((value) => value !== null);
  if (populated.length > 0 && populated.every((value) => typeof value === 'boolean')) return 'BOOLEAN';
  if (populated.length > 0 && populated.every((value) => typeof value === 'number')) {
    return populated.every((value) => Number.isInteger(value)) ? 'INTEGER' : 'DECIMAL';
  }
  if (populated.length > 0 && populated.every((value) => typeof value === 'string' && isStrictIsoDate(value))) {
    return 'DATE';
  }
  if (populated.length > 0 && populated.every((value) => typeof value === 'string' && isStrictIsoDateTime(value))) {
    return 'DATETIME';
  }
  return 'TEXT';
}

function isStrictIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function isStrictIsoDateTime(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(Z|[+-]\d{2}:\d{2})$/.exec(value);
  if (!match) return false;
  const [, year, month, day, hour, minute, second, milliseconds = '0', offset] = match;
  if (!isStrictIsoDate(`${year}-${month}-${day}`)) return false;
  if (Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59) return false;
  if (offset !== 'Z') {
    const offsetMatch = /^([+-]\d{2}):(\d{2})$/.exec(offset);
    if (!offsetMatch || Number(offsetMatch[1].slice(1)) > 23 || Number(offsetMatch[2]) > 59) return false;
  }
  return !Number.isNaN(Date.parse(
    `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds.padEnd(3, '0')}${offset}`,
  ));
}

/** DataTable dışındaki ürün ekranlarını da güvenli v2 Output snapshot akışına bağlar. */
export function buildDataTableOutputDataset(records: unknown): DataTableOutputDataset {
  if (!Array.isArray(records) || records.length === 0) return { columns: [], rows: [] };
  const objectRecords = records.filter(
    (record): record is Record<string, unknown> => Boolean(record) && typeof record === 'object' && !Array.isArray(record),
  );
  if (objectRecords.length !== records.length) {
    return {
      columns: [{ key: 'value', label: 'Value', type: 'TEXT' }],
      rows: records.map((record) => [outputCellValue(record)]),
    };
  }
  const keys = Array.from(new Set(objectRecords.flatMap((record) => Object.keys(record))))
    .filter((key) => key.toLowerCase() !== 'actions' && !isSensitiveDataTableOutputIdentifier(key));
  if (keys.length === 0) return { columns: [], rows: [] };
  const rows = objectRecords.map((record) => keys.map((key) => outputCellValue(record[key])));
  return {
    columns: keys.map((key, index) => ({
      key,
      label: key,
      type: inferOutputColumnType(rows.map((row) => row[index])),
    })),
    rows,
  };
}
